import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import { io } from 'socket.io-client';
import { GoogleMap, MarkerF, PolylineF, OverlayViewF } from '@react-google-maps/api';
import './TrackOrder.css';
import StarRating from '../components/StarRating.jsx';

// Use centralized API service
import { getOrderById, createReview, createDeliveryPersonReview } from '../api/apiService';
import { formatPrice } from '../utils/formatPrice';
import { getCountryCodeForCountry } from '../utils/countryUtils';

const containerStyle = { width: '100%', height: '320px' };
const defaultMapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
};
const DELIVERY_PERSON_EMOJI = "🛵";
const userLocationIcon = {
  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" view
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="24">🏠</text>
    </svg>
  `),
  scaledSize: { width: 32, height: 32 },
};
const deliveryPersonIcon = {
  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="24">${DELIVERY_PERSON_EMOJI}</text>
    </svg>
  `),
  scaledSize: { width: 32, height: 32 },
};
// ✅ NEW: Icon for the Vendor/Store
const vendorLocationIcon = {
  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="24">📍</text>
    </svg>
  `),
  scaledSize: { width: 32, height: 32 },
};
const DEFAULT_CENTER = { lat: 17.385044, lng: 78.486671 };
const SOCKET_SERVER_URL = 'https://vinirji.com'; // Update with your actual Socket.IO server URL

// ✅ NEW: Use this for serving images from the root server path
const SERVER_ROOT_URL = 'https://vinirji.com';

const RIDER_MEDIA_URL = 'https://riderapi.vinirji.com'; // <-- New constant for rider media URL

// Helper to fetch image and convert to Base64 for PDF
const getBase64ImageFromURL = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL("image/jpeg");
      resolve(dataURL);
    };
    img.onerror = error => {
      console.warn("Failed to load image for PDF:", error);
      resolve(null); // Resolve null so PDF generation continues without image
    };
    img.src = url;
  });
};

const TrackOrder = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deliveryPersonLiveLocation, setDeliveryPersonLiveLocation] = useState(null);
  const [eta, setEta] = useState(null);
  const [routePath, setRoutePath] = useState([]);

  // VENDOR RATING STATE
  const [vendorRating, setVendorRating] = useState(0);
  const [vendorComment, setVendorComment] = useState('');
  const [submittingVendorRating, setSubmittingVendorRating] = useState(false);
  const [vendorRatingSuccess, setVendorRatingSuccess] = useState(false);
  const [vendorRatingError, setVendorRatingError] = useState(null);

  // DELIVERY PERSON RATING STATE
  const [deliveryPersonRating, setDeliveryPersonRating] = useState(0);
  const [deliveryPersonComment, setDeliveryPersonComment] = useState('');
  const [submittingDeliveryPersonRating, setSubmittingDeliveryPersonRating] = useState(false);
  const [deliveryPersonRatingSuccess, setDeliveryPersonRatingSuccess] = useState(false);
  const [deliveryPersonRatingError, setDeliveryPersonRatingError] = useState(null);

  const socketRef = useRef(null);
  const mapRef = useRef(null);
  const deliveryPersonMarkerRef = useRef(null);

  const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);
  const onMapUnmount = useCallback(() => { mapRef.current = null; }, []);
  const onDeliveryPersonMarkerLoad = useCallback(marker => { deliveryPersonMarkerRef.current = marker; }, []);
  const onDeliveryPersonMarkerUnmount = useCallback(() => { deliveryPersonMarkerRef.current = null; }, []);

  
 
// 1. Define fetchOrder with 'silent' mode
const fetchOrder = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true); 
    setError(null);
    try {
        const res = await getOrderById(orderId);
        if (res.data.success && res.data.data) {
            setOrder(res.data.data);
            
            // Ratings state
            if (res.data.data.vendor_rating) {
                setVendorRating(res.data.data.vendor_rating);
                setVendorRatingSuccess(true);
            }
            if (res.data.data.delivery_person_rating) {
                setDeliveryPersonRating(res.data.data.delivery_person_rating);
                setDeliveryPersonRatingSuccess(true);
            }
            // Live Location
            if (res.data.data.delivery_person_current_location?.coordinates) {
                setDeliveryPersonLiveLocation({
                    lat: res.data.data.delivery_person_current_location.coordinates[1],
                    lng: res.data.data.delivery_person_current_location.coordinates[0],
                });
            }
        } else {
            if (!isBackground) setError(res.data.message || 'Order not found.');
        }
    } catch (err) {
        if (!isBackground) {
            setError(err?.response?.data?.message || 'Order not found or access denied.');
        }
    } finally {
        if (!isBackground) setLoading(false);
    }
}, [orderId]);



// 2. Initial Load Effect
useEffect(() => {
    fetchOrder(false); // Normal load (shows spinner)
}, [fetchOrder]);




// 3. Robust Socket Connection & Event Handling
useEffect(() => {
    const token = localStorage.getItem('customer_token');
    
    socketRef.current = io(SOCKET_SERVER_URL, { 
        auth: { token },
        transports: ['websocket', 'polling'] 
    });

    socketRef.current.on('connect', () => {
        socketRef.current.emit('joinOrderRoom', orderId);
    });

    // --- HANDLE STATUS UPDATES (Optimistic) ---
    socketRef.current.on('orderStatusUpdate', (data) => {
        if (data.orderId === orderId) {
            setOrder(prevOrder => {
                if (!prevOrder) return prevOrder;
                const newHistory = [
                    ...(prevOrder.status_history || []), 
                    { status: data.status, updatedAt: new Date().toISOString() }
                ];
                return { 
                    ...prevOrder, 
                    status: data.status, 
                    status_history: newHistory 
                };
            });
            fetchOrder(true); // Silent refresh
        }
    });

    // --- HANDLE FULL UPDATES ---
    socketRef.current.on('orderUpdate', (updatedOrder) => {
        if (updatedOrder._id === orderId || updatedOrder.id === orderId) {
            setOrder(updatedOrder); 
        }
    });

    // --- HANDLE RIDER LOCATION ---
    socketRef.current.on('deliveryPersonLocationUpdate', (locationData) => {
        if (locationData.orderId === orderId) {
            const newLoc = { lat: locationData.lat, lng: locationData.lng };
            setDeliveryPersonLiveLocation(newLoc);
            if (deliveryPersonMarkerRef.current) {
                deliveryPersonMarkerRef.current.setPosition(newLoc);
            }
        }
    });

    return () => {
        if (socketRef.current) socketRef.current.disconnect();
    };
}, [orderId, fetchOrder]);

   

  const deliveryAddressCoords = useMemo(() => {
    const coordinates = order?.delivery_address_snapshot?.location?.coordinates;
    if (coordinates && Array.isArray(coordinates) && coordinates.length === 2) {
      return { lat: coordinates[1], lng: coordinates[0] };
    }
    return null;
  }, [order]);

  // ✅ NEW: Extract Vendor Coordinates
  const vendorCoords = useMemo(() => {
    // MongoDB stores GeoJSON as [lng, lat]
    const coordinates = order?.vendorLocation?.coordinates;
    if (coordinates && Array.isArray(coordinates) && coordinates.length === 2) {
      return { lat: coordinates[1], lng: coordinates[0] };
    }
    return null;
  }, [order]);

  useEffect(() => {
    if (
      deliveryPersonLiveLocation &&
      deliveryAddressCoords &&
      window.google &&
      window.google.maps
    ) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: deliveryPersonLiveLocation,
          destination: deliveryAddressCoords,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === "OK" && result.routes.length > 0) {
            setEta(result.routes[0].legs[0].duration.text);
            setRoutePath(result.routes[0].overview_path.map(p => ({ lat: p.lat(), lng: p.lng() })));
          } else {
            setEta(null);
            setRoutePath([]);
          }
        }
      );
    } else {
      setEta(null);
      setRoutePath([]);
    }
  }, [deliveryPersonLiveLocation, deliveryAddressCoords]);

  useEffect(() => {
    if (mapRef.current && deliveryAddressCoords) {
      mapRef.current.panTo(deliveryAddressCoords);
      mapRef.current.setZoom(15);
    }
  }, [deliveryAddressCoords]);

  const currencySymbol = order?.currencySymbol || '₹';

  const getCustomerNameForDisplay = (order) => {
    if (!order) return 'Customer';
    return order.user_name || order.delivery_address_snapshot?.name || order.user_id?.first_name || 'Customer';
  };

  // PDF logic
  const handleDownloadReceipt = async () => { // ✅ Made async
    if (!order) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const leftMargin = 40;
    const rightMargin = 40;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight(); // Get page height
    const rightAlignX = pageWidth - rightMargin;
    let currentY = 40;
    const sectionTitleSize = 14;
    const bodyFontSize = 11;
    const lineHeight = 12;
    const sectionGap = 10;

    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#333333');
    doc.text('Order Receipt', pageWidth / 2, currentY, { align: 'center' });
    currentY += 30;

    doc.setFontSize(sectionTitleSize);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#2c3e50');
    doc.text('Order Details', leftMargin, currentY);
    currentY += lineHeight + 8;

    doc.setFontSize(bodyFontSize);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#555555');

    const orderDateObj = order.completedAt
      ? new Date(order.completedAt)
      : order.createdAt
        ? new Date(order.createdAt)
        : null;

    let dateStr = '';
    let timeStr = '';
    if (orderDateObj) {
      // --- START OF THE FIX ---

      // 1. Define the list of countries that use MM/DD/YYYY format.
      const usaStyleDateCountries = ['united states', 'canada'];

      const country = order?.delivery_address_snapshot?.country || '';
      const day = String(orderDateObj.getDate()).padStart(2, '0');
      const month = String(orderDateObj.getMonth() + 1).padStart(2, '0');
      const year = orderDateObj.getFullYear();

      // 2. Check if the order's country is in the list.
      if (usaStyleDateCountries.includes(country.toLowerCase().trim())) {
        // Format for USA & Canada: MM/DD/YYYY
        dateStr = `${month}/${day}/${year}`;
      } else {
        // Default/Indian format: DD/MM/YYYY
        dateStr = `${day}/${month}/${year}`;
      }

      const timeLocale = usaStyleDateCountries.includes(country.toLowerCase().trim()) ? 'en-US' : 'en-IN';
      timeStr = orderDateObj.toLocaleTimeString(timeLocale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      // --- END OF THE FIX ---
    }

    doc.text(`Order ID: ${order._id || order.id || ''}`, leftMargin, currentY);
    currentY += lineHeight;
    const customerNamePdf = getCustomerNameForDisplay(order);
    doc.text(`Customer Name: ${customerNamePdf}`, leftMargin, currentY);
    currentY += lineHeight;

    // Replace customerPhone with country-code aware variant for PDF
    const rawCustomerPhone = order.user_phone || order.delivery_address_snapshot?.phone_number || '';
    const pdfCountry = order.delivery_address_snapshot?.country || '';
    const pdfCountryCode = getCountryCodeForCountry(pdfCountry);
    const customerPhoneWithCode = rawCustomerPhone ? `${pdfCountryCode} ${rawCustomerPhone}`.trim() : '';

    if (customerPhoneWithCode) {
      doc.text(`Phone: ${customerPhoneWithCode}`, leftMargin, currentY);
      currentY += lineHeight;
    }

    doc.text(`Order Placed On: ${dateStr} at ${timeStr}`, leftMargin, currentY);
    currentY += lineHeight;
    doc.text(`Vendor: ${order.vendorName || order.vendor_name || 'Store'}`, leftMargin, currentY);
    currentY += lineHeight;
    doc.text(`Order Status: ${order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1).replace(/_/g, ' ') : 'N/A'}`, leftMargin, currentY);
    currentY += lineHeight;

    // UPDATED: Include delivery person name in the PDF
    if (order.delivery_boy_id) {
      const deliveryPersonName = order.deliveryPersonName || `${order.delivery_boy_id.first_name || ''} ${order.delivery_boy_id.last_name || ''}`.trim();
      if (deliveryPersonName) {
        doc.text(`Delivery Partner: ${deliveryPersonName}`, leftMargin, currentY);
        currentY += lineHeight;
      }
    }

    const pdfAddressParts = [];
    if (order.delivery_address_snapshot?.door_number) pdfAddressParts.push(order.delivery_address_snapshot.door_number);
    if (order.delivery_address_snapshot?.street_name) pdfAddressParts.push(order.delivery_address_snapshot.street_name);
    else if (order.delivery_address_snapshot?.street) pdfAddressParts.push(order.delivery_address_snapshot.street);
    if (order.delivery_address_snapshot?.locality) pdfAddressParts.push(order.delivery_address_snapshot.locality);
    if (order.delivery_address_snapshot?.city) pdfAddressParts.push(order.delivery_address_snapshot.city);
    if (order.delivery_address_snapshot?.state) pdfAddressParts.push(order.delivery_address_snapshot.state); // <-- Ensure state is included
    if (order.delivery_address_snapshot?.zip_code) pdfAddressParts.push(order.delivery_address_snapshot.zip_code);
    if (order.delivery_address_snapshot?.country) pdfAddressParts.push(order.delivery_address_snapshot.country); // <-- Ensure country is included

    const fullAddress = `Delivery Address: ${pdfAddressParts.filter(Boolean).join(', ')}`;
    const wrappedAddress = doc.splitTextToSize(fullAddress, pageWidth - leftMargin - rightMargin);
    doc.text(wrappedAddress, leftMargin, currentY);
    currentY += wrappedAddress.length * lineHeight;
    currentY += sectionGap;

    doc.setDrawColor('#cccccc');
    doc.line(leftMargin, currentY, rightAlignX, currentY);
    currentY += 13;

    const qtyColumnX = leftMargin;
    const itemColumnX = leftMargin + 40;
    const spicyLevelColumnX = leftMargin + 200;
    const priceColumnX = leftMargin + 350;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor('#333333');
    doc.text('Qty', qtyColumnX, currentY);
    doc.text('Item', itemColumnX, currentY);
    doc.text('Spicy Level', spicyLevelColumnX, currentY);
    doc.text(`Price (${currencySymbol})`, priceColumnX, currentY);
    currentY += 18;

    doc.line(leftMargin, currentY, rightAlignX, currentY);
    currentY += 13;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(bodyFontSize);
    doc.setTextColor('#444444');

    // PDF-specific price formatting helper
    const formatPdfPrice = (amount) => {
      const formattedAmount = Number(amount).toFixed(2);
      if (order.currency === 'CAD') {
        return `${formattedAmount} ${currencySymbol}`;
      }
      const symbol = order.currency === 'INR' ? 'Rs.' : currencySymbol;
      return `${symbol}${formattedAmount}`;
    };

    order.items?.forEach(item => {
      let itemName = `${item.name}`;
      if (item.variant && item.variant.trim()) {
        itemName += ` (${item.variant.trim()})`;
      }
      let displaySpicyLevel = item.spicyLevel && item.spicyLevel.trim() ? item.spicyLevel.trim().charAt(0).toUpperCase() + item.spicyLevel.trim().slice(1) : 'N/A';
      const itemPrice = formatPdfPrice(item.price_per_unit * item.quantity);
      const maxItemNameWidth = spicyLevelColumnX - itemColumnX - 10;
      const maxSpicyLevelWidth = priceColumnX - spicyLevelColumnX - 10;
      const maxPriceWidth = rightAlignX - priceColumnX - 10;

      doc.text(`${item.quantity}`, qtyColumnX, currentY);
      doc.text(itemName, itemColumnX, currentY, { maxWidth: maxItemNameWidth });
      doc.text(displaySpicyLevel, spicyLevelColumnX, currentY, { maxWidth: maxSpicyLevelWidth });
      doc.text(itemPrice, priceColumnX, currentY, { maxWidth: maxPriceWidth });
      currentY += lineHeight;
    });

    currentY += sectionGap;
    doc.line(leftMargin, currentY, rightAlignX, currentY);
    currentY += 13;

    doc.setFontSize(sectionTitleSize);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#2c3e50');
    doc.text('Summary', leftMargin, currentY);
    currentY += lineHeight + 8;

    doc.setFontSize(bodyFontSize);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#555555');

    const addBreakdownRow = (label, value) => {
      doc.text(`${label} ${formatPdfPrice(value !== undefined && value !== null ? value : 0)}`, leftMargin, currentY);
      currentY += lineHeight;
    };

    addBreakdownRow('Subtotal:', order.subtotal);
    addBreakdownRow('Discount:', order.total_discount);

    // Estimated Tax placeholder for USD & CAD (PDF)
    if (order?.currency === 'USD' || order?.currency === 'CAD') {
      addBreakdownRow('Estimated Tax:', 0);
    }

    addBreakdownRow('Platform Fee + GST:', order.isFirstOrder
      ? 0
      : (order.platform_fee || 0) + (order.tax_amount || 0));
    addBreakdownRow('Delivery Fee:', order.delivery_charges);
    addBreakdownRow('Tip:', order.tip);
    if (orderTaxes > 0) {
      addBreakdownRow('Taxes:', orderTaxes);
    }

    currentY += 10;
    doc.setDrawColor('#e0e0e0');
    doc.setLineDash([2, 2], 0);
    doc.line(leftMargin, currentY, rightAlignX, currentY);
    doc.setLineDash([], 0);
    currentY += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor('#27ae60');
    doc.text(`Total: ${formatPdfPrice(order.net_amount)}`, leftMargin, currentY);
    doc.setFont('helvetica', 'normal');
    currentY += sectionGap + 5;

    doc.line(leftMargin, currentY, rightAlignX, currentY);
    currentY += 13;

    doc.setFontSize(sectionTitleSize);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#2c3e50');
    doc.text('Payment Information', leftMargin, currentY);
    currentY += lineHeight + 8;

    doc.setFontSize(bodyFontSize);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#555555');

    let pdfPaymentMethod = '';
    // COD support disabled: comment out explicit COD label and normalize COD to Online Payment
    /*
    if (order.payment_mode === 'COD') {
      pdfPaymentMethod = 'Cash on Delivery';
    }
    */
    // Consolidates Stripe, Razorpay and legacy COD values into a single generic term for the PDF
    if (order.payment_mode === 'Stripe' || order.payment_mode === 'Razorpay' || order.payment_mode === 'COD') {
      pdfPaymentMethod = 'Online Payment';
    } else {
      pdfPaymentMethod = order.payment_mode || 'Online';
    }

    doc.text(`Method: ${pdfPaymentMethod} ${order.card_last4 ? `.... ${order.card_last4}` : ''}`, leftMargin, currentY);
    currentY += lineHeight;
    doc.text(`Amount Paid: ${formatPdfPrice(order.net_amount)}`, leftMargin, currentY);
    
    // Show refund info for rejected orders. Previously refunds were skipped for COD.
    // Since COD is disabled we remove the COD-exclusion (commented below) and always show refund block for rejected orders.
    // (If you later re-enable COD, re-add `&& order.payment_mode !== 'COD'` to keep original behavior.)
    // if (order.status === 'rejected' && order.payment_mode !== 'COD') {
    if (order.status === 'rejected') {
      currentY += lineHeight; // Add a small gap
      
      doc.setDrawColor('#e0e0e0');
      doc.setLineDash([2, 2], 0);
      doc.line(leftMargin, currentY, rightAlignX, currentY);
      doc.setLineDash([], 0);
      currentY += lineHeight;

      doc.setFont('helvetica', 'bold');
      doc.setTextColor('#c0392b'); // Red color for attention
      doc.text('Refund Status:', leftMargin, currentY);
      currentY += lineHeight;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor('#555555');
      const refundMessage = `As this order was rejected, a refund for your payment has been initiated. You can expect the amount to be credited to your original payment method within 2-3 business days. For any further queries, please contact our support team at support@vinirji.com.`;
      
      const wrappedText = doc.splitTextToSize(refundMessage, pageWidth - leftMargin - rightMargin);
      doc.text(wrappedText, leftMargin, currentY);
      currentY += wrappedText.length * lineHeight; // Adjust Y position based on text height
    }

    // ✅ NEW: Delivery Proof Image Logic
    if (order.status === 'delivered') {
      const proofPath = order.deliveryPhoto || order.rider?.deliveryProofImageUrl;

      if (proofPath) {
        currentY += sectionGap;

        // Separator line
        doc.setDrawColor('#e0e0e0');
        doc.setLineDash([2, 2], 0);
        doc.line(leftMargin, currentY, rightAlignX, currentY);
        doc.setLineDash([], 0);
        currentY += 15; // Reduced from 20

        // Section Title
        doc.setFontSize(sectionTitleSize);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor('#2c3e50');
        doc.text('Delivery Confirmation', leftMargin, currentY);
        currentY += lineHeight + 5;

        // Fetch Image
        const cleanPath = proofPath.replace(/\\/g, '/');
        const imageUrl = `${RIDER_MEDIA_URL}/${cleanPath}`;

        try {
          const imgBase64 = await getBase64ImageFromURL(imageUrl);

          if (imgBase64) {
            const imgProps = doc.getImageProperties(imgBase64);

            // 1. Reserve strict space for the Footer at the bottom (60pt)
            const footerBuffer = 60;
            const captionSpace = 20;

            // 2. Calculate remaining height on Page 1
            const maxAvailableHeight = pageHeight - currentY - footerBuffer - captionSpace;

            // 3. Set Initial Target Width
            let finalWidth = 220;
            let finalHeight = (imgProps.height * finalWidth) / imgProps.width;

            // 4. CRITICAL: Force scaling if image is too tall
            if (finalHeight > maxAvailableHeight) {
              finalHeight = maxAvailableHeight;
              finalWidth = (imgProps.width * finalHeight) / imgProps.height;
            }

            // 5. Render Image (Only if we have positive space)
            if (finalHeight > 10) {
              doc.addImage(imgBase64, 'JPEG', leftMargin, currentY, finalWidth, finalHeight);

              doc.setFontSize(9);
              doc.setFont('helvetica', 'italic');
              doc.setTextColor('#888888');
              doc.text('Photo taken at the time of delivery', leftMargin, currentY + finalHeight + 10);
            }
          }
        } catch (imgErr) {
          console.warn('Could not add delivery proof to PDF:', imgErr);
        }
      }
    }

    // --- Footer Logic ---
    // REMOVED: The check for doc.addPage(). We strictly force it to Page 1.

    // Position footer at the absolute bottom of Page 1
    let footerY = pageHeight - 35;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#999999');
    doc.text('Thank you for ordering with us!', pageWidth / 2, footerY, { align: 'center' });

    doc.setTextColor('#007bff');
    doc.setFontSize(11);

    doc.textWithLink('vinirji.com', pageWidth / 2, footerY + 12, {
      align: 'center',
      url: 'https://vinirji.com'
    });

    doc.save(`OrderReceipt_${order._id || order.id || 'order'}.pdf`);
  };

  // Vendor rating
  const handleVendorRatingSubmit = async () => {
    setSubmittingVendorRating(true);
    setVendorRatingError(null);
    try {
        if (!order) {
            setVendorRatingError('Order information missing.');
            setSubmittingVendorRating(false);
            return;
        }
        if (vendorRating === 0) {
            setVendorRatingError('Please provide a rating.');
            setSubmittingVendorRating(false);
            return;
        }

        // Only send essential info: orderId, rating, comment
        const reviewData = {
            orderId: order._id,
            rating: vendorRating,
            comment: vendorComment,
        };

        const response = await createReview(reviewData);
        if (response.data.success) {
            setVendorRatingSuccess(true);
            setOrder({ ...order, vendor_rating_submitted: true, vendor_rating: vendorRating });
        } else {
            setVendorRatingError(response.data.message || 'Failed to submit rating.');
        }
    } catch (err) {
        setVendorRatingError(err?.response?.data?.message || 'An error occurred while submitting your rating.');
    } finally {
        setSubmittingVendorRating(false);
    }
  };

  // Delivery person rating handler
  const handleDeliveryPersonRatingSubmit = async () => {
    setSubmittingDeliveryPersonRating(true);
    setDeliveryPersonRatingError(null);

    try {
      if (!order) {
        setDeliveryPersonRatingError('Order information missing.');
        setSubmittingDeliveryPersonRating(false);
        return;
      }
      if (deliveryPersonRating === 0) {
        setDeliveryPersonRatingError('Please provide a rating.');
        setSubmittingDeliveryPersonRating(false);
        return;
      }

      const reviewData = {
        orderId: order._id,
        rating: deliveryPersonRating,
        comment: deliveryPersonComment,
      };

      const res = await createDeliveryPersonReview(reviewData);

      if (res.data && res.data.success) {
        setDeliveryPersonRatingSuccess(true);
        setOrder({
          ...order,
          delivery_person_rating_submitted: true,
          delivery_person_rating: deliveryPersonRating,
        });
      } else {
        setDeliveryPersonRatingError(res.data?.message || 'Failed to submit rating.');
      }
    } catch (err) {
      setDeliveryPersonRatingError(err?.response?.data?.message || 'An error occurred while submitting your rating.');
    } finally {
      setSubmittingDeliveryPersonRating(false);
    }
  };

  if (loading) return <div className="track-order-loading">Loading order details...</div>;
  if (error) return (
    <div className="track-order-error">
      <p>{error}</p>
      <p>Order ID: <b>{orderId}</b></p>
    </div>
  );
  if (!order) return null;
  const orderTaxes = order.taxes !== undefined && order.taxes !== null
    ? Number(order.taxes)
    : Math.round((
        (Number(order.subtotal || 0) - Number(order.total_discount || 0) +
          Number(order.platform_fee || 0) + Number(order.delivery_charges || 0) +
          Number(order.tip || 0)) * 0.05
      ) * 100) / 100;
  if (!deliveryAddressCoords) {
    return (
      <div className="track-order-loading">
        Waiting for delivery address location...
      </div>
    );
  }

  // Replace raw delivery phone computation with country-code aware variant
  const rawPhoneNumber = order?.user_phone || order?.delivery_address_snapshot?.phone_number || '';
  const deliveryCountry = order?.delivery_address_snapshot?.country || '';
  const countryCode = getCountryCodeForCountry(deliveryCountry);
  const deliveryPhoneNumberWithCode = rawPhoneNumber ? `${countryCode} ${rawPhoneNumber}`.trim() : '';

  const customerNameHtml = order.user_name || getCustomerNameForDisplay(order);
  const deliveryDoorNumber = order.delivery_address_snapshot?.door_number || '';
  const deliveryStreetName = order.delivery_address_snapshot?.street_name || '';
  const deliveryLocality = order.delivery_address_snapshot?.locality || '';
  const deliveryCity = order.delivery_address_snapshot?.city || '';
  const deliveryState = order.delivery_address_snapshot?.state || '';
  const deliveryZipCode = order.delivery_address_snapshot?.zip_code || '';
  const deliveryCountryForDisplay = order.delivery_address_snapshot?.country || '';
  const addressComponentsForDisplay = [];
  if (deliveryDoorNumber) addressComponentsForDisplay.push(deliveryDoorNumber);
  if (deliveryStreetName) addressComponentsForDisplay.push(deliveryStreetName);
  else if (order.delivery_address_snapshot?.street) addressComponentsForDisplay.push(order.delivery_address_snapshot.street);
  if (deliveryLocality) addressComponentsForDisplay.push(deliveryLocality);
  if (deliveryCity) addressComponentsForDisplay.push(deliveryCity);
  if (deliveryState) addressComponentsForDisplay.push(deliveryState);
  if (deliveryZipCode) addressComponentsForDisplay.push(deliveryZipCode);
  if (deliveryCountryForDisplay) addressComponentsForDisplay.push(deliveryCountryForDisplay);
  const fullDeliveryAddressForDisplay = addressComponentsForDisplay.filter(Boolean).join(', ');
  const estimatedDeliveryTimeDisplay = order.estimatedDeliveryTime || 'Not available';
  const paymentDate = order.completedAt
    ? new Date(order.completedAt).toLocaleDateString('en-IN', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      }) + ' ' + new Date(order.completedAt).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    : '';
  const finalStatuses = ['delivered', 'rejected', 'cancelled', 'payment_failed'];

  return (
    <div className="track-order-container">
      {/* Left: Map and Address Info */}
      <div className="track-order-map">
        <div className="map-placeholder">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={deliveryPersonLiveLocation || deliveryAddressCoords || DEFAULT_CENTER}
            zoom={15}
            options={defaultMapOptions}
            onLoad={onMapLoad}
            onUnmount={onMapUnmount}
          >
            {/* User/Delivery Location Marker */}
            {deliveryAddressCoords && (
              <MarkerF
                position={deliveryAddressCoords}
                icon={userLocationIcon}
                title="Delivery Location"
              />
            )}

            {/* ✅ NEW: Vendor Location Marker */}
            {vendorCoords && (
              <MarkerF
                position={vendorCoords}
                icon={vendorLocationIcon}
                title={order?.vendorName || "Store Location"}
              />
            )}

            {/* Delivery Person Marker */}
            {deliveryPersonLiveLocation && (
              <MarkerF
                position={deliveryPersonLiveLocation}
                icon={deliveryPersonIcon}
                title="Delivery Person"
                onLoad={onDeliveryPersonMarkerLoad}
                onUnmount={onDeliveryPersonMarkerUnmount}
              />
            )}
            {/* ETA Overlay */}
            {deliveryPersonLiveLocation && eta && (
              <OverlayViewF
                position={deliveryPersonLiveLocation}
                mapPaneName={OverlayViewF.OVERLAY_MOUSE_TARGET}
              >
                <div style={{
                  padding: '6px 10px',
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 7px 1px rgba(0,0,0,0.2)',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  color: '#27ae60',
                  transform: 'translate(-50%, -150%)'
                }}>
                  ETA: {eta}
                </div>
              </OverlayViewF>
            )}
            {/* Polyline for route */}
            {routePath.length > 0 && (
              <PolylineF
                path={routePath}
                options={{
                  strokeColor: "#4285F4",
                  strokeOpacity: 0.7,
                  strokeWeight: 5,
                }}
              />
            )}
          </GoogleMap>
        </div>
        <div className="map-delivery-address-info" style={{
          marginTop: '15px',
          padding: '10px 15px',
          backgroundColor: '#f8f8f8',
          border: '1px solid #eee'
        }}>
          <div style={{ fontWeight: 'bold', fontSize: '1.1em', marginBottom: '5px' }}>Delivery Details:</div>
          {customerNameHtml && (
            <div style={{ fontSize: '0.95em', color: '#333', marginBottom: '5px' }}>
              For: <b>{customerNameHtml}</b>
            </div>
          )}
          {fullDeliveryAddressForDisplay && (
            <div style={{ fontSize: '0.95em', color: '#333' }}>
              Address: <b>{fullDeliveryAddressForDisplay}</b>
            </div>
          )}
          {deliveryPhoneNumberWithCode && (
            <div style={{ fontSize: '0.95em', color: '#333', marginTop: '5px' }}>
              Contact: <b>{deliveryPhoneNumberWithCode}</b>
            </div>
          )}
           {!finalStatuses.includes(order.status) && (
    <div style={{ fontSize: '0.95em', color: '#333', marginTop: '5px' }}>
      Est. Delivery: <b>{estimatedDeliveryTimeDisplay}</b>
    </div>
  )}
        </div>

        {/* ✅ Delivery Confirmation section */}
        {order.status === 'delivered' && (order.deliveryPhoto || order.rider?.deliveryProofImageUrl) && (
          <div className="order-section order-delivery-proof">
            <div className="section-title">
              Delivery Confirmation
            </div>
            <div className="delivery-proof-image-container">
  <img
    /* 1. Get the path string 
       2. Use .replace(/\\/g, '/') to turn Windows backslashes (\) into web slashes (/)
       3. Use .replace(/^\//, '') to ensure there isn't a leading slash that causes a double // 
    */
    src={`${RIDER_MEDIA_URL}/${(order.deliveryPhoto || order.rider?.deliveryProofImageUrl || '')
      .replace(/\\/g, '/')
      .replace(/^\//, '')}`} 
    alt="Proof of delivery"
    className="delivery-proof-image"
    onError={(e) => { 
      console.log("Image failed to load:", e.target.src);
      e.target.style.display = 'none'; 
    }} 
  />
  <p>Photo taken at the time of delivery.</p>
</div>
          </div>
        )}
      </div>

      {/* Right: Order Summary */}
      <div className="track-order-summary">
        {/* Header */}
        <div className="order-header-details">
          <h2 className="order-complete-title">
            Order Status: {order.status && order.status.charAt(0).toUpperCase() + order.status.slice(1).replace(/_/g, ' ')}
            {order.status === 'delivered' ? ' 🎉' : ''}
          </h2>
          <div className="order-meta">
            <span className="order-id">
              <b>Order ID:</b> {order._id || order.id}
            </span>
            {order.completedAt && (
              <span className="order-date-main">
                Delivered On: <b>{new Date(order.completedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</b>
              </span>
            )}
            {!order.completedAt && order.createdAt && (
              <span className="order-date-main">
                Placed On: <b>{new Date(order.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</b>
              </span>
            )}
          </div>
          <div className="order-status-msg">
            {
              order.status === 'delivered' ? 'Your order has been successfully delivered. Enjoy!' :
              order.status === 'rejected' ? 'Your order has been rejected. For more details, please contact support at vinirji@support.com.' :
              order.status === 'cancelled' ? 'Your order is cancelled.' :
              order.status === 'payment_failed' ? 'This order has been canceled. For more details, please contact support.' :
              `Your order is currently ${order.status.charAt(0).toUpperCase() + order.status.slice(1).replace(/_/g, ' ')}. We will keep you updated!`
            }
          </div>
          {/* <div className="order-actions">
            {finalStatuses.includes(order.status) && (
              <button
                onClick={handleDownloadReceipt}
                className="btn btn-primary-alt highlight-button"
              >
                Download Receipt
              </button>
            )}
          </div> */}
        </div>

        {/* Delivery Person Info */}
        {!finalStatuses.includes(order.status) && (
          <div className="order-section order-delivery-section">
            <div className="section-title">
              Delivery Person
            </div>
            {order.delivery_boy_id ? (
              <>
                <div className="delivery-person-info">
                  <span className="icon">🚚</span>
                  <span>
                    {order.deliveryPersonName ||
                      `${order.delivery_boy_id.first_name || ''} ${order.delivery_boy_id.last_name || ''}`.trim()}
                  </span>
                </div>
                {order.status === 'out_for_delivery' && (
                  <div className="delivery-status">
                    <span className="delivery-status-dot"></span>
                    {order.delivery_status &&
                      order.delivery_status.charAt(0).toUpperCase() +
                        order.delivery_status.slice(1).replace(/_/g, ' ')}{' '}
                    {order.delivery_status === 'delivered' && <span className="check-icon">✓</span>}
                  </div>
                )}
              </>
            ) : (
              <div className="delivery-person-info">
                <span className="icon">⏱️</span>
                <span>Delivery partner will be assigned shortly...</span>
              </div>
            )}
          </div>
        )}

        {/* Vendor Rating Section */}
        {order.status === 'delivered' && (
          <div className="order-section order-delivery-rating-section">
            <div className="vendor-details">
              <span className="icon">🛍️</span>
              <div>
                <b className="vendor-name">{order.vendorName || order.vendor_name || 'Store'}</b>
                <div className="vendor-items-count">
                  {order.items?.length || 0} item{order.items?.length === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            <div className="rating-area" style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#f9f9f9',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}>
              {order.vendor_rating_submitted ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '1.1em',
                  fontWeight: '600',
                  color: '#333',
                }}>
                  <span style={{ fontSize: '1.4em', color: '#f39c12' }}>⭐</span>
                  You rated this store {order.vendor_rating} stars!
                </div>
              ) : (
                <>
                  <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                    <StarRating
                      initialRating={vendorRating}
                      onRatingChange={setVendorRating}
                    />
                  </div>
                  <div style={{ width: '100%', marginBottom: '12px' }}>
                    <textarea
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        resize: 'vertical',
                        fontSize: '0.9em',
                        color: '#444',
                      }}
                      value={vendorComment}
                      onChange={(e) => setVendorComment(e.target.value)}
                      placeholder="Share your feedback (optional)"
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px' }}>
                    <button
                      onClick={handleVendorRatingSubmit}
                      disabled={vendorRating === 0 || submittingVendorRating}
                      style={{
                        width: '50%',
                        padding: '10px 20px',
                        fontWeight: '600',
                        color: 'white',
                        transition: 'background-color 0.25s ease',
                        backgroundColor: (vendorRating === 0 || submittingVendorRating) ? '#cccccc' : '#27ae60',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (vendorRating === 0 || submittingVendorRating) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {submittingVendorRating ? 'Submitting...' : 'Submit Rating'}
                    </button>
                    <button
                      onClick={() => {setVendorRating(0); setVendorComment(''); setVendorRatingError(null);}}
                      style={{
                        width: '50%',
                        padding: '10px 20px',
                        fontWeight: '600',
                        color: '#555',
                        transition: 'background-color 0.25s ease',
                        backgroundColor: '#e0e0e0',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  {vendorRatingError && <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '8px' }}>{vendorRatingError}</p>}
                  {vendorRatingSuccess && <p style={{ color: '#22c55e', fontSize: '0.875rem', marginTop: '8px' }}>Thank you for your feedback!</p>}
                </>
              )}
            </div>
          </div>
        )}

        {/* Delivery Person Rating Section */}
        {order.status === 'delivered' && order.delivery_boy_id && (
          <div className="order-section order-delivery-rating-section">
            <div className="vendor-details">
              <span className="icon">🚚</span>
              <div>
                <b className="vendor-name">Your Delivery Partner</b>
                <div className="vendor-items-count">
                  {order.deliveryPersonName}
                </div>
              </div>
            </div>
            <div className="rating-area" style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#f9f9f9',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}>
              {order.delivery_person_rating_submitted ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '1.1em',
                  fontWeight: '600',
                  color: '#333',
                }}>
                  <span style={{ fontSize: '1.4em', color: '#f39c12' }}>⭐</span>
                  You rated your delivery partner {order.delivery_person_rating} stars!
                </div>
              ) : (
                <>
                  <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                    <StarRating
                      initialRating={deliveryPersonRating}
                      onRatingChange={setDeliveryPersonRating}
                    />
                  </div>
                  <div style={{ width: '100%', marginBottom: '12px' }}>
                    <textarea
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        resize: 'vertical',
                        fontSize: '0.9em',
                        color: '#444',
                      }}
                      value={deliveryPersonComment}
                      onChange={(e) => setDeliveryPersonComment(e.target.value)}
                      placeholder="Share your feedback (optional)"
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px' }}>
                    <button
                      onClick={handleDeliveryPersonRatingSubmit}
                      disabled={deliveryPersonRating === 0 || submittingDeliveryPersonRating}
                      style={{
                        width: '50%',
                        padding: '10px 20px',
                        fontWeight: '600',
                        color: 'white',
                        transition: 'background-color 0.25s ease',
                        backgroundColor: (deliveryPersonRating === 0 || submittingDeliveryPersonRating) ? '#cccccc' : '#27ae60',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (deliveryPersonRating === 0 || submittingDeliveryPersonRating) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {submittingDeliveryPersonRating ? 'Submitting...' : 'Submit Rating'}
                    </button>
                    <button
                      onClick={() => {setDeliveryPersonRating(0); setDeliveryPersonComment(''); setDeliveryPersonRatingError(null);}}
                      style={{
                        width: '50%',
                        padding: '10px 20px',
                        fontWeight: '600',
                        color: '#555',
                        transition: 'background-color 0.25s ease',
                        backgroundColor: '#e0e0e0',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  {deliveryPersonRatingError && <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '8px' }}>{deliveryPersonRatingError}</p>}
                  {deliveryPersonRatingSuccess && <p style={{ color: '#22c55e', fontSize: '0.875rem', marginTop: '8px' }}>Thank you for your feedback!</p>}
                </>
              )}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="order-section order-items-list">
          <div className="section-title">
            Items
          </div>
          <div className="items-wrapper">
            {order.items?.map(item => (
              <div
                key={item._id || item.item_id}
                className="order-item-row"
              >
                <div>
                  <span className="item-qty">{item.quantity}x</span> {item.name}{' '}
                  {item.variant ? <span className="item-variant">- {item.variant}</span> : ''}
                  {item.spicyLevel && item.spicyLevel.trim() && (
                    <span className="item-spicy-level">
                      | Spicy: {item.spicyLevel.trim()}
                    </span>
                  )}
                </div>
                <div className="item-price">{formatPrice(item.price_per_unit * item.quantity, order.currency, currencySymbol)}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Breakdown */}
        <div className="order-section order-breakdown-section">
          <div className="section-title">
            Breakdown
          </div>
          <div className="breakdown-details">
            <div className="breakdown-row">
              <span>Item Total</span> <span>{formatPrice(order.subtotal, order.currency, currencySymbol)}</span>
            </div>
            <div className="breakdown-row">
              <span>Discount</span> <span>- {formatPrice(order.total_discount, order.currency, currencySymbol)}</span>
            </div>

            {/* --- MODIFIED BLOCK for USD & CAD --- */}
            {(order?.currency === 'USD' || order?.currency === 'CAD') && (
                <div className="breakdown-row">
                    <span>Estimated Tax</span>
                    <span>{formatPrice(0, order.currency, currencySymbol)}</span>
                </div>
            )}
            {/* --- END OF BLOCK --- */}

            <div className="breakdown-row">
              <span>Platform Fee + GST</span>
              <span>
                {order.isFirstOrder ? (
                  <>
                    <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px' }}>
                      {formatPrice(15 + (order.tax_amount || 0), order.currency, currencySymbol)}
                    </span>
                    <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                      {formatPrice(0, order.currency, currencySymbol)}
                    </span>
                  </>
                ) : (
                  formatPrice((order.platform_fee || 0) + (order.tax_amount || 0), order.currency, currencySymbol)
                )}
              </span>
            </div>
            <div className="breakdown-row">
              <span>Delivery Fee</span> <span>{formatPrice(order.delivery_charges, order.currency, currencySymbol)}</span>
            </div>
            <div className="breakdown-row">
              <span>Delivery Tip</span> <span>{formatPrice(order.tip, order.currency, currencySymbol)}</span>
            </div>
            {orderTaxes > 0 && (
              <div className="breakdown-row">
                <span>Taxes</span> <span>{formatPrice(orderTaxes, order.currency, currencySymbol)}</span>
              </div>
            )}
          </div>
        </div>
        {/* Payment */}
        <div className="order-section order-payment-section">
          <div className="section-title">
            Payment
          </div>
          <div className="payment-details">
            <div className="payment-method">
              <span className="icon">💳</span>
              {/* COD support disabled in UI:
                  Original code showed "Cash on Delivery" when payment_mode === 'COD'.
                  We normalize COD to a generic Online Payment label to avoid exposing COD option in UI.
              */}
              { (order.payment_mode && order.payment_mode !== 'COD')
                  ? ((order.payment_mode === 'Stripe' || order.payment_mode === 'Razorpay') ? 'Online Payment' : order.payment_mode)
                  : 'Online Payment'
              } {order.card_last4 ? `.... ${order.card_last4}` : ''}
            </div>
            <span className="payment-date">{paymentDate}</span>
          </div>

          <div className="payment-amount">
            {formatPrice(order.net_amount, order.currency, currencySymbol)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackOrder;
